import React from 'react';

// Stylized lotus flower — the sacred symbol of Goddess Lakshmi
const LotusIcon = ({ className = '', size = 32, color = '#D48C57' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {/* Center petal */}
    <path
      d="M32 4C32 4 26 18 26 28C26 34 28.7 38 32 38C35.3 38 38 34 38 28C38 18 32 4 32 4Z"
      fill={color}
      opacity="0.95"
    />
    {/* Left petal */}
    <path
      d="M18 16C18 16 16 30 20 36C23 40.5 27 40 29 38C24 36 22 30 22 26C22 22 18 16 18 16Z"
      fill={color}
      opacity="0.7"
    />
    {/* Right petal */}
    <path
      d="M46 16C46 16 48 30 44 36C41 40.5 37 40 35 38C40 36 42 30 42 26C42 22 46 16 46 16Z"
      fill={color}
      opacity="0.7"
    />
    {/* Far left petal */}
    <path
      d="M10 24C10 24 12 34 18 38C22 40.5 25 39 26 37C21 36 17 32 15 28C13 25 10 24 10 24Z"
      fill={color}
      opacity="0.5"
    />
    {/* Far right petal */}
    <path
      d="M54 24C54 24 52 34 46 38C42 40.5 39 39 38 37C43 36 47 32 49 28C51 25 54 24 54 24Z"
      fill={color}
      opacity="0.5"
    />
    {/* Base / water line */}
    <path
      d="M14 44C14 44 20 40 32 40C44 40 50 44 50 44"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity="0.6"
    />
    <path
      d="M18 48C18 48 23 45 32 45C41 45 46 48 46 48"
      stroke={color}
      strokeWidth="1.2"
      strokeLinecap="round"
      opacity="0.35"
    />
    {/* Divine center */}
    <circle cx="32" cy="24" r="2.5" fill={color} opacity="0.9" />
  </svg>
);

const SIZES = {
  sm: { lotus: 26, name: 'text-sm sm:text-base', tagline: 'text-[8px]', gap: 'gap-1.5' },
  md: { lotus: 32, name: 'text-base sm:text-xl', tagline: 'text-[9px] sm:text-[11px]', gap: 'gap-2' },
  lg: { lotus: 44, name: 'text-xl sm:text-3xl', tagline: 'text-[10px] sm:text-xs', gap: 'gap-2.5' },
  xl: { lotus: 56, name: 'text-2xl sm:text-4xl lg:text-5xl', tagline: 'text-xs sm:text-sm', gap: 'gap-3' },
};

const COLORS = {
  default: { lotus: '#D48C57', text: '#1A2520', tagline: '#5C6A61' },
  white: { lotus: '#D48C57', text: '#FFFFFF', tagline: 'rgba(255,255,255,0.75)' },
  dark: { lotus: '#D48C57', text: '#FDFBF7', tagline: 'rgba(253,251,247,0.6)' },
  hero: { lotus: '#D48C57', text: '#FFFFFF', tagline: 'rgba(255,255,255,0.8)' },
};

// Full logo with lotus + text
export function Logo({
  variant = 'default',
  size = 'md',
  showTagline = true,
  className = '',
}) {
  const s = SIZES[size];
  const c = COLORS[variant];

  return (
    <div className={`flex items-center ${s.gap} ${className}`} data-testid="logo">
      <LotusIcon size={s.lotus} color={c.lotus} />
      <div className="flex flex-col">
        <span
          className={`font-serif ${s.name} font-black leading-tight tracking-tight`}
          style={{ color: c.text }}
        >
          Lakshmi Nivasa
        </span>
        {showTagline && (
          <span
            className={`${s.tagline} font-semibold uppercase tracking-[0.2em] leading-none mt-0.5`}
            style={{ color: c.tagline }}
          >
            Premium Villa Plots
          </span>
        )}
      </div>
    </div>
  );
}

// Standalone lotus mark
export function LogoMark({ size = 32, color = '#D48C57', className = '' }) {
  return <LotusIcon size={size} color={color} className={className} />;
}

// Tamil variant
export function LogoTamil({
  variant = 'default',
  size = 'md',
  showTagline = true,
  className = '',
}) {
  const s = SIZES[size];
  const c = COLORS[variant];

  return (
    <div className={`flex items-center ${s.gap} ${className}`} data-testid="logo-tamil">
      <LotusIcon size={s.lotus} color={c.lotus} />
      <div className="flex flex-col">
        <span
          className={`font-serif ${s.name} font-black leading-tight tracking-tight`}
          style={{ color: c.text }}
        >
          {'\u0BB2\u0B95\u0BCD\u0BB7\u0BCD\u0BAE\u0BBF \u0BA8\u0BBF\u0BB5\u0BBE\u0BB8\u0BBE'}
        </span>
        {showTagline && (
          <span
            className={`${s.tagline} font-semibold uppercase tracking-[0.15em] leading-none mt-0.5`}
            style={{ color: c.tagline }}
          >
            {'\u0BAA\u0BBF\u0BB0\u0BC0\u0BAE\u0BBF\u0BAF\u0BAE\u0BCD \u0BB5\u0BBF\u0BB2\u0BCD\u0BB2\u0BBE \u0BAA\u0BBF\u0BB3\u0BBE\u0B9F\u0BCD\u0B9F\u0BC1\u0B95\u0BB3\u0BCD'}
          </span>
        )}
      </div>
    </div>
  );
}

// Language-aware logo
export default function LanguageLogo({ lang = 'en', ...props }) {
  if (lang === 'ta') return <LogoTamil {...props} />;
  return <Logo {...props} />;
}

export { LotusIcon };
